import { formatTextForDisplay } from '@shared/utils/helpers/format-text-for-display';
import type { AxiosError } from 'axios';

type ErrorResponse = {
  detail?: string;
  message?: string;
  errors?: Record<string, string[]>;
  statusCode?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export function getErrorMessage(error: AxiosError | Error): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error && !('response' in error)) {
    return error.message || 'An unknown error occurred.';
  }

  if (error.response?.status === 500) {
    return 'Internal server error occurred.';
  }

  if (typeof error === 'object' && error !== null) {
    const responseError = error.response?.data as ErrorResponse;

    if ('codename' in error) {
      return `You don't have permission to ${formatTextForDisplay(error.codename as string)} `;
    }

    if (responseError?.detail) {
      if (Array.isArray(responseError?.detail)) {
        return responseError?.detail.join(', ');
      }

      const match = responseError?.detail?.match(/\{.*\}/);
      if (match) {
        try {
          const parsedObject = JSON?.parse(match[0]?.replace(/'/g, '"'));
          const mappedErrors = Object?.entries(parsedObject)
            .map(([key, messages]) => {
              const errorMessages = Array?.isArray(messages)
                ? messages?.join(', ')
                : String(messages);
              return `${key}: ${errorMessages}`;
            })
            .join('; ');
          return mappedErrors;
        } catch {
          return responseError?.detail;
        }
      }
      return responseError?.detail;
    }

    if (responseError?.message) {
      return responseError?.message;
    }

    if (responseError?.errors) {
      const firstValidationError = Object?.values(
        responseError?.errors
      ).flat()[0];
      return firstValidationError || 'Validation error occurred.';
    }

    if (responseError?.statusCode) {
      return `Request failed with status code ${responseError?.statusCode}.`;
    }

    if (responseError?.message) {
      return responseError?.message;
    }

    const dynamicErrorKeys = Object?.keys(responseError)?.filter(
      (key) => key !== 'message' && key !== 'errors' && key !== 'statusCode'
    );
    if (dynamicErrorKeys?.length > 0) {
      return dynamicErrorKeys
        ?.map((key) => `${key}: ${responseError[key]}`)
        ?.join('; ');
    }
  }

  return 'An unknown error occurred.';
}
